import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [isCompany, setIsCompany] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [orgName, setOrgName] = useState("");
  const [gstin, setGstin] = useState("");
  const [billing, setBilling] = useState("");
  const [isApprover, setIsApprover] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.patch("/users/me", {
        name,
        email: email || undefined,
        is_company: isCompany,
        org_name: isCompany ? orgName : undefined,
        gstin: isCompany ? gstin : undefined,
        billing_address: isCompany ? billing : undefined,
        is_approver: isApprover,
      });
      setUser(data);
      toast.success("Profile saved.");
      nav("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink flex flex-col">
      <div className="max-w-xl mx-auto w-full px-6 py-10">
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Welcome</p>
        <h1 className="font-serif text-3xl mb-2">Tell us about you</h1>
        <p className="text-light-ink text-sm mb-8">We'll personalise pricing, invoices and approvals.</p>

        <form onSubmit={save} className="space-y-6">
          <div className="tablo-card p-5 flex items-center justify-between" data-testid="onboarding-company-toggle-row">
            <div>
              <p className="font-medium">Booking for a company</p>
              <p className="text-xs text-light-ink/80">Adds GST invoicing, HR approvals, org billing.</p>
            </div>
            <Switch checked={isCompany} onCheckedChange={setIsCompany} data-testid="onboarding-company-switch" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Your name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white border-tablo-border" data-testid="onboarding-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.in" className="bg-white border-tablo-border" data-testid="onboarding-email-input" />
            </div>
          </div>

          {isCompany && (
            <div className="space-y-4 tablo-card p-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Company name</label>
                <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="bg-white border-tablo-border" data-testid="onboarding-org-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">GSTIN</label>
                <Input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="36AAACO1234A1Z5" className="bg-white border-tablo-border" data-testid="onboarding-gstin-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Billing address</label>
                <Input value={billing} onChange={(e) => setBilling(e.target.value)} className="bg-white border-tablo-border" data-testid="onboarding-billing-input" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <div>
                  <p className="text-sm font-medium">I'm an HR approver</p>
                  <p className="text-xs text-light-ink/80">See team requests and approve in one tap.</p>
                </div>
                <Switch checked={isApprover} onCheckedChange={setIsApprover} data-testid="onboarding-approver-switch" />
              </div>
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full tablo-btn-primary" data-testid="onboarding-save-btn">
            {loading ? "Saving..." : "Continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
